/**
 * Helper to calculate distance between two points.
 */
export function getDistance(p1, p2) {
    const dx = p1[0] - p2[0];
    const dy = p1[1] - p2[1];
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Projects point P onto line segment AB.
 * Returns the nearest point on the segment, and the distance.
 */
export function projectPointOnSegment(p, a, b) {
    const ax = a[0];
    const ay = a[1];
    const bx = b[0];
    const by = b[1];
    const px = p[0];
    const py = p[1];

    const abx = bx - ax;
    const aby = by - ay;
    const ab2 = abx * abx + aby * aby;

    if (ab2 === 0) {
        return { point: a, distance: getDistance(p, a) };
    }

    let t = ((px - ax) * abx + (py - ay) * aby) / ab2;
    t = Math.max(0, Math.min(1, t));

    const projX = ax + t * abx;
    const projY = ay + t * aby;
    const projPoint = [projX, projY];

    return {
        point: projPoint,
        distance: getDistance(p, projPoint)
    };
}

/**
 * Snaps cursor coordinates to the nearest vertex (Corner) or edge (Along-the-line).
 */
export function findSnapPoint(cursorCoords, dxfLayers, visibleLayers, snapRadius) {
    if (!dxfLayers || !visibleLayers || visibleLayers.length === 0) {
        return null;
    }

    let bestSnap = null;
    let minCornerDist = snapRadius;
    let minEdgeDist = snapRadius;

    // Check corners
    for (const layerName of visibleLayers) {
        const entities = dxfLayers[layerName] || [];
        for (const ent of entities) {
            if (ent.points && ent.points.length > 0) {
                for (const pt of ent.points) {
                    const dist = getDistance(cursorCoords, pt);
                    if (dist < minCornerDist) {
                        minCornerDist = dist;
                        bestSnap = {
                            point: pt,
                            type: 'corner',
                            entity: ent
                        };
                    }
                }
            }
        }
    }

    if (bestSnap) {
        return bestSnap;
    }

    // Check along-the-line
    for (const layerName of visibleLayers) {
        const entities = dxfLayers[layerName] || [];
        for (const ent of entities) {
            if (ent.points && ent.points.length >= 2) {
                for (let i = 0; i < ent.points.length - 1; i++) {
                    const p1 = ent.points[i];
                    const p2 = ent.points[i + 1];
                    const { point, distance } = projectPointOnSegment(cursorCoords, p1, p2);
                    if (distance < minEdgeDist) {
                        minEdgeDist = distance;
                        bestSnap = {
                            point: point,
                            type: 'edge',
                            entity: ent
                        };
                    }
                }
                if (ent.closed && ent.points.length >= 3) {
                    const p1 = ent.points[ent.points.length - 1];
                    const p2 = ent.points[0];
                    const { point, distance } = projectPointOnSegment(cursorCoords, p1, p2);
                    if (distance < minEdgeDist) {
                        minEdgeDist = distance;
                        bestSnap = {
                            point: point,
                            type: 'edge',
                            entity: ent
                        };
                    }
                }
            }
        }
    }

    return bestSnap;
}

/**
 * Snap to polygon corners/edges (usable area, sub-areas, boundary).
 * Corner priority over along-the-line, matching DXF snap behavior.
 */
export function findSnapOnPolygons(cursorCoords, polygons, snapRadius) {
    if (!polygons || polygons.length === 0) return null;

    const { corners, edges } = (() => {
        const c = [];
        const e = [];
        polygons.forEach((poly) => {
            if (!poly || poly.length < 2) return;
            poly.forEach((pt) => c.push(pt));
            for (let i = 0; i < poly.length; i++) {
                e.push([poly[i], poly[(i + 1) % poly.length]]);
            }
        });
        return { corners: c, edges: e };
    })();

    let bestSnap = null;
    let minCornerDist = snapRadius;

    for (const pt of corners) {
        const dist = getDistance(cursorCoords, pt);
        if (dist < minCornerDist) {
            minCornerDist = dist;
            bestSnap = { point: pt, type: 'corner' };
        }
    }
    if (bestSnap) return bestSnap;

    let minEdgeDist = snapRadius;
    for (const [a, b] of edges) {
        const { point, distance } = projectPointOnSegment(cursorCoords, a, b);
        if (distance < minEdgeDist) {
            minEdgeDist = distance;
            bestSnap = { point, type: 'edge' };
        }
    }
    return bestSnap;
}

/**
 * Combined snap: DXF layers first, then polygon boundaries.
 * Pass excludePolygons to skip module edges during free module placement.
 */
export function findSnapPointWithPolygons(
    cursorCoords,
    dxfLayers,
    visibleLayers,
    snapRadius,
    extraPolygons = [],
    excludePolygons = [],
) {
    const dxfSnap = findSnapPoint(cursorCoords, dxfLayers, visibleLayers, snapRadius);
    if (dxfSnap) return dxfSnap;
    const snap = findSnapOnPolygons(cursorCoords, extraPolygons, snapRadius);
    if (!snap || !excludePolygons?.length) return snap;

    const [px, py] = snap.point;
    const onExcludedModule = excludePolygons.some((poly) => {
        if (!poly || poly.length < 3) return false;
        for (let i = 0; i < poly.length; i++) {
            const a = poly[i];
            const b = poly[(i + 1) % poly.length];
            const { distance } = projectPointOnSegment([px, py], a, b);
            if (distance < snapRadius * 0.5) return true;
        }
        return false;
    });
    return onExcludedModule ? null : snap;
}
